import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../lib/api';
import { FileText, RefreshCw, Search, Send, PencilLine, Euro, Printer, Check, Plus, LayoutTemplate, Trash2, CalendarPlus } from 'lucide-react';
import { ReservationDialog, NewReservationDialog } from '../components/ReservationDialogs';
import StandaloneOfferDialog from '../components/StandaloneOfferDialog';
import OfferTemplatesDialog from '../components/OfferTemplatesDialog';

function eur(v) {
  return Number(v || 0).toFixed(2).replace('.', ',') + ' €';
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

function qtyStr(q) {
  const n = Number(q) || 0;
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace('.', ',');
}

function fmtDateLong(d) {
  if (!d) return '';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
}

// Baut einen 80mm-Kassenbon (HTML) für ein Angebot und öffnet den Druckdialog.
function printReceipt80mm({ reservation, offer, restaurantName, logoUrl }) {
  const items = (offer?.items || []).map((it) => {
    const lt = it.line_total != null ? it.line_total : (Number(it.quantity || 0) * Number(it.unit_price || 0));
    return `
      <div class="item">
        <div class="desc">${escapeHtml(it.description)}</div>
        <div class="row"><span>${qtyStr(it.quantity)} \u00d7 ${eur(it.unit_price)}</span><span>${eur(lt)}</span></div>
      </div>`;
  }).join('');

  const end = reservation?.end_time ? ` \u2013 ${escapeHtml(reservation.end_time)}` : '';
  const note = (offer?.note || '').trim();
  const logoBlock = logoUrl ? `<img class="logo" src="${escapeHtml(logoUrl)}" alt="" />` : '';

  const html = `<!DOCTYPE html><html lang="de"><head><meta charset="utf-8"><title>Angebot ${escapeHtml(reservation?.customer_name || '')}</title>
<style>
  @page { size: 80mm auto; margin: 3mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html,body { margin:0; padding:0; }
  body { width: 72mm; font-family: 'Courier New', Courier, monospace; color:#000; font-size:15px; font-weight:700; line-height:1.4; }
  .center { text-align:center; }
  .logo { display:block; margin:0 auto 4px; max-width:38mm; max-height:20mm; filter:grayscale(1) contrast(1.4); }
  .name { font-size:22px; font-weight:900; }
  .label { letter-spacing:3px; font-size:15px; font-weight:900; margin-top:2px; }
  hr { border:none; border-top:2px solid #000; margin:6px 0; }
  .meta div { display:flex; justify-content:space-between; gap:8px; font-weight:700; }
  .meta b { font-weight:900; }
  .item { margin:5px 0; }
  .desc { font-weight:900; word-break:break-word; }
  .row { display:flex; justify-content:space-between; gap:8px; font-weight:700; }
  .total { font-size:20px; font-weight:900; }
  .note { white-space:pre-line; margin-top:6px; font-size:14px; font-weight:700; }
  .foot { margin-top:10px; font-size:13px; font-weight:700; text-align:center; }
</style></head><body>
  <div class="center">
    ${logoBlock}
    <div class="name">${escapeHtml(restaurantName || 'La Contadina')}</div>
    <div class="label">ANGEBOT</div>
  </div>
  <hr>
  <div class="meta">
    <div><span>Für</span><b>${escapeHtml(reservation?.customer_name || '')}</b></div>
    <div><span>Datum</span><b>${escapeHtml(fmtDateLong(reservation?.date))}</b></div>
    <div><span>Uhrzeit</span><b>${escapeHtml(reservation?.start_time || '')}${end}</b></div>
    <div><span>Personen</span><b>${escapeHtml(String(reservation?.persons ?? ''))}</b></div>
  </div>
  <hr>
  ${items}
  <hr>
  <div class="row total"><span>GESAMT</span><span>${eur(offer?.total)}</span></div>
  ${note ? `<div class="note">${escapeHtml(note)}</div>` : ''}
  <div class="foot">Dieses Angebot ist freibleibend.<br>Vielen Dank &amp; bis bald!</div>
  <script>window.onload=function(){setTimeout(function(){window.focus();window.print();},200);};window.onafterprint=function(){window.close();};</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=380,height=640');
  if (!w) return false;
  w.document.open();
  w.document.write(html);
  w.document.close();
  return true;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    return new Date(d + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return d; }
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const STATUS_FILTERS = [
  { key: 'all', label: 'Alle' },
  { key: 'draft', label: 'Entwürfe' },
  { key: 'sent', label: 'Gesendet' },
  { key: 'accepted', label: 'Angenommen' },
];

export default function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const [openResv, setOpenResv] = useState(null);
  const [showStandalone, setShowStandalone] = useState(false);
  const [editStandaloneId, setEditStandaloneId] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [convertOffer, setConvertOffer] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('La Contadina');
  const [logoUrl, setLogoUrl] = useState('');
  const metaLoadedRef = useRef(false);

  useEffect(() => {
    // Brand logo for the receipt: always use the app's bundled logo (reliable),
    // it is generated from the restaurant's uploaded logo.
    setLogoUrl(`${window.location.origin}/logo192.png`);
    api.get('/settings/public')
      .then(r => { if (r.data?.restaurant_name) setRestaurantName(r.data.restaurant_name); })
      .catch(() => {});
  }, []);

  const ensureMeta = useCallback(async () => {
    if (metaLoadedRef.current) return;
    try {
      const [a, t] = await Promise.all([api.get('/areas'), api.get('/tables')]);
      setAreas(a.data || []);
      setTables(t.data || []);
      metaLoadedRef.current = true;
    } catch { /* ignore */ }
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get('/reservation-offers');
      setOffers(r.data?.offers || []);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    ensureMeta();
  }, [load, ensureMeta]);

  const openRow = async (o) => {
    if (o.source === 'standalone') {
      setEditStandaloneId(o.offer_id);
      setShowStandalone(true);
      return;
    }
    await ensureMeta();
    try {
      const r = await api.get(`/reservations/${o.reservation_id}`);
      setOpenResv(r.data);
    } catch { /* ignore */ }
  };

  const printOffer = async (o) => {
    try {
      if (o.source === 'standalone') {
        const r = await api.get(`/standalone-offers/${o.offer_id}`);
        const d = r.data;
        if (!d?.items?.length) return;
        printReceipt80mm({
          reservation: { customer_name: d.customer_name, date: d.event_date, start_time: d.event_time, end_time: null, persons: d.persons },
          offer: { items: d.items, note: d.note, total: d.total },
          restaurantName, logoUrl,
        });
      } else {
        const r = await api.get(`/reservations/${o.reservation_id}`);
        const resv = r.data;
        if (!resv?.offer?.items?.length) return;
        printReceipt80mm({ reservation: resv, offer: resv.offer, restaurantName, logoUrl });
      }
    } catch { /* ignore */ }
  };

  const deleteOffer = async (o) => {
    if (o.source !== 'standalone' || !o.offer_id) return;
    if (!window.confirm(`Angebot für „${o.customer_name || 'Gast'}" wirklich löschen? Das kann nicht rückgängig gemacht werden.`)) return;
    setDeletingId(o.offer_id);
    try {
      await api.delete(`/standalone-offers/${o.offer_id}`);
      setOffers((prev) => prev.filter((x) => x.offer_id !== o.offer_id));
    } catch { /* ignore */ } finally {
      setDeletingId(null);
    }
  };

  const startConvert = async (o) => {
    await ensureMeta();
    setConvertOffer(o);
  };

  const q = query.trim().toLowerCase();
  const filtered = offers.filter((o) => {
    if (filter !== 'all' && o.status !== filter) return false;
    if (!q) return true;
    return [o.customer_name, o.email, o.phone].some(v => (v || '').toLowerCase().includes(q));
  });

  const totalCount = offers.length;
  const sentCount = offers.filter(o => o.status === 'sent').length;
  const draftCount = offers.filter(o => o.status === 'draft').length;
  const acceptedCount = offers.filter(o => o.status === 'accepted').length;
  const totalValue = offers.reduce((s, o) => s + Number(o.total || 0), 0);

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="admin-offers">
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Verwaltung</p>
          <h1 className="font-display text-3xl text-[#1C1C19] flex items-center gap-2">
            <FileText className="w-7 h-7" /> Angebote
          </h1>
          <p className="text-sm text-[#5C5C59] mt-1">Alle Angebote — an Reservierungen und eigenständige (ohne Reservierung).</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="offers-refresh"
            className="inline-flex items-center gap-2 border border-[#E5E5E0] px-4 py-2.5 rounded text-sm hover:bg-[#F9F9F7] transition"
          >
            <RefreshCw className="w-4 h-4" /> Aktualisieren
          </button>
          <button
            onClick={() => setShowTemplates(true)}
            data-testid="offers-templates-btn"
            className="inline-flex items-center gap-2 border border-[#E5E5E0] px-4 py-2.5 rounded text-sm hover:bg-[#F9F9F7] transition"
          >
            <LayoutTemplate className="w-4 h-4" /> Vorlagen
          </button>
          <button
            onClick={() => { setEditStandaloneId(null); setShowStandalone(true); }}
            data-testid="offers-new-btn"
            className="inline-flex items-center gap-2 bg-[#1C1C19] text-white px-4 py-2.5 rounded text-sm hover:bg-[#3A3A35] transition"
          >
            <Plus className="w-4 h-4" /> Neues Angebot
          </button>
        </div>
      </div>

      {/* Kennzahlen */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="offers-stat-total">
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Angebote</p>
          <p className="text-2xl font-semibold text-[#1C1C19]">{totalCount}</p>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="offers-stat-draft">
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Entwürfe</p>
          <p className="text-2xl font-semibold text-[#64748B]">{draftCount}</p>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="offers-stat-sent">
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Gesendet</p>
          <p className="text-2xl font-semibold text-[#0369A1]">{sentCount}</p>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="offers-stat-accepted">
          <p className="text-xs uppercase tracking-wider text-[#5C5C59]">Angenommen</p>
          <p className="text-2xl font-semibold text-[#16A34A]">{acceptedCount}</p>
        </div>
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-4" data-testid="offers-stat-value">
          <p className="text-xs uppercase tracking-wider text-[#5C5C59] flex items-center gap-1"><Euro className="w-3 h-3" /> Gesamtwert</p>
          <p className="text-2xl font-semibold text-[#1C1C19]">{eur(totalValue)}</p>
        </div>
      </div>

      {/* Filter + Suche */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-1 bg-[#F1F5F9] rounded-lg p-1" data-testid="offers-filter">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              data-testid={`offers-filter-${f.key}`}
              className={`px-3 py-1.5 rounded-md text-sm transition ${filter === f.key ? 'bg-white text-[#1C1C19] shadow-sm font-medium' : 'text-[#64748B] hover:text-[#1C1C19]'}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name, E-Mail oder Telefon suchen"
            data-testid="offers-search"
            className="w-full pl-9 pr-3 py-2 border border-[#E5E5E0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1C1C19]/10"
          />
        </div>
      </div>

      {/* Tabelle */}
      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm" data-testid="offers-table">
            <thead>
              <tr className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#5C5C59]">
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Reservierung</th>
                <th className="px-4 py-3">Gast</th>
                <th className="px-4 py-3 text-center">Pers.</th>
                <th className="px-4 py-3 text-center">Positionen</th>
                <th className="px-4 py-3 text-right">Betrag</th>
                <th className="px-4 py-3">Aktualisiert</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#9CA3AF]">Wird geladen…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-[#9CA3AF]" data-testid="offers-empty">
                  Keine Angebote gefunden.
                </td></tr>
              )}
              {!loading && filtered.map((o) => {
                const rowKey = o.reservation_id || o.offer_id;
                return (
                <tr key={rowKey} className="border-t border-[#F1F1EE] hover:bg-[#F9F9F7] transition" data-testid={`offer-row-${rowKey}`}>
                  <td className="px-4 py-3">
                    {o.status === 'accepted' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-[#DCFCE7] text-[#166534]">
                        <Check className="w-3 h-3" /> Angenommen
                      </span>
                    ) : o.status === 'sent' ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-[#E0F2FE] text-[#075985]">
                        <Send className="w-3 h-3" /> Gesendet
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-[#F1F5F9] text-[#475569]">
                        <PencilLine className="w-3 h-3" /> Entwurf
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#1C1C19]">
                    {o.date ? <>{fmtDate(o.date)}<span className="text-[#9CA3AF]"> · {o.start_time || '—'}</span></> : <span className="text-[#9CA3AF]">ohne Reservierung</span>}
                    {o.source === 'standalone' && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider text-[#B45309] bg-[#FEF3C7] px-1.5 py-0.5 rounded">Frei</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1C1C19]">{o.customer_name || '—'}</div>
                    <div className="text-xs text-[#9CA3AF]">{o.email || o.phone || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-[#5C5C59]">{o.persons ?? '—'}</td>
                  <td className="px-4 py-3 text-center text-[#5C5C59]">{o.item_count}</td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1C1C19]">{eur(o.total)}</td>
                  <td className="px-4 py-3 text-[#5C5C59]">
                    {o.status === 'accepted' ? `Angenommen ${fmtDateTime(o.accepted_at)}` : o.status === 'sent' ? `Gesendet ${fmtDateTime(o.sent_at)}` : fmtDateTime(o.updated_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1.5">
                      {o.source === 'standalone' && (
                        <button
                          onClick={() => startConvert(o)}
                          data-testid={`offer-convert-${rowKey}`}
                          title="In eine Reservierung umwandeln"
                          className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white transition"
                        >
                          <CalendarPlus className="w-3.5 h-3.5" /> Reservierung
                        </button>
                      )}
                      <button
                        onClick={() => printOffer(o)}
                        data-testid={`offer-print-${rowKey}`}
                        title="Als 80mm-Bon drucken"
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[#E5E5E0] text-[#1C1C19] hover:bg-[#F1F5F9] transition"
                      >
                        <Printer className="w-3.5 h-3.5" /> Bon 80mm
                      </button>
                      <button
                        onClick={() => openRow(o)}
                        data-testid={`offer-open-${rowKey}`}
                        className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-[#1C1C19] text-[#1C1C19] hover:bg-[#1C1C19] hover:text-white transition"
                      >
                        <FileText className="w-3.5 h-3.5" /> Öffnen
                      </button>
                      {o.source === 'standalone' && (
                        <button
                          onClick={() => deleteOffer(o)}
                          disabled={deletingId === o.offer_id}
                          data-testid={`offer-delete-${rowKey}`}
                          title="Angebot löschen"
                          className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded border border-[#FCA5A5] text-[#DC2626] hover:bg-[#DC2626] hover:text-white transition disabled:opacity-50"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {openResv && (
        <ReservationDialog
          open={!!openResv}
          onClose={() => setOpenResv(null)}
          reservation={openResv}
          tables={tables}
          areas={areas}
          onUpdate={() => { load(); setOpenResv(null); }}
        />
      )}

      <StandaloneOfferDialog
        open={showStandalone}
        onClose={() => { setShowStandalone(false); setEditStandaloneId(null); }}
        offerId={editStandaloneId}
        onSaved={load}
      />

      <OfferTemplatesDialog open={showTemplates} onClose={() => setShowTemplates(false)} />

      {convertOffer && (
        <NewReservationDialog
          open={!!convertOffer}
          onClose={() => setConvertOffer(null)}
          tables={tables}
          areas={areas}
          defaultName={convertOffer.customer_name || ''}
          defaultEmail={convertOffer.email || ''}
          defaultPhone={convertOffer.phone || ''}
          defaultPersons={convertOffer.persons}
          defaultDate={convertOffer.date || ''}
          defaultTime={convertOffer.start_time || ''}
          onCreated={() => { setConvertOffer(null); load(); }}
        />
      )}
    </div>
  );
}
